import type { HttpClient } from '@wix/cli-http-client';
import pRetry from 'p-retry';
import type { Options } from 'p-retry';
import { kebabCase } from 'lodash';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import {
  createAppDeployment,
  completeAppDeployment,
} from '@wix/ambassador-velo-backend-v1-app-deployment/http';
import type {
  StaticFileMetadata,
  AppDeployment,
} from '@wix/ambassador-velo-backend-v1-app-deployment/types';
import { createAppProject } from '@wix/ambassador-velo-backend-v1-app-project/http';
import {
  getAppEnvironmentByName,
  upsertAppEnvironment,
  removeAppEnvironmentVariables,
} from '@wix/ambassador-velo-backend-v1-app-environment/http';
import {
  AppEnvironmentSchema,
  CompleteAppDeploymentSchema,
  CreateAppDeploymentSchema,
  CreateAppProjectSchema,
  StaticFileUploadSchema,
} from './schemas.js';

const { minRetryTimeout } = getTestOverrides();
// Velo hosting project type "App"
const APP_PROJECT_TYPE_ID = '35252494-7615-4117-9c4c-53ae7d3be95f';

export class BackendAsAServiceClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    this.retryOptions = {
      retries: 3,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  createAppProject = async (appId: string, projectName: string) => {
    try {
      const { data } = await this.httpClient.request(
        createAppProject({
          appProject: {
            id: appId,
            displayName: projectName,
            slug: kebabCase(projectName),
            appProjectTypeId: APP_PROJECT_TYPE_ID,
          },
        })
      );

      return CreateAppProjectSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedCreatingAppProject(),
        cause: e,
      });
    }
  };

  createAppDeployment = async (
    appId: string,
    staticFilesMetadata: StaticFileMetadata[]
  ) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            createAppDeployment({
              appDeployment: {
                appProjectId: appId,
                staticFilesMetadata,
              },
            })
          ),
        this.retryOptions
      );

      return CreateAppDeploymentSchema.parse(data);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedCreatingAppDeployment(),
        cause: e,
      });
    }
  };

  completeAppDeployment = async (
    appDeployment: AppDeployment,
    staticsCompletionToken: string
  ) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            completeAppDeployment({
              appDeployment,
              staticsCompletionToken,
            })
          ),
        this.retryOptions
      );

      return CompleteAppDeploymentSchema.parse(data).appDeployment;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedFinalizingAppDeployment(),
        cause: e,
      });
    }
  };

  postForm = async (endpointUrl: string, token: string, formData: FormData) => {
    try {
      const responseJson = await pRetry(
        async () => {
          const response = await fetch(endpointUrl, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          });

          if (!response.ok) {
            throw new Error(response.statusText);
          }

          return response.json();
        },
        { retries: 3 }
      );

      return StaticFileUploadSchema.parse(responseJson);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToUploadStaticFiles(),
        cause: e,
      });
    }
  };

  // Env management

  getAppEnvironmentByName = async (appId: string, environment: string) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            getAppEnvironmentByName({
              appProjectId: appId,
              appEnvironmentName: environment,
            })
          ),
        this.retryOptions
      );

      return AppEnvironmentSchema.parse(data).appEnvironment;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToPullEnvironment(),
        cause: e,
      });
    }
  };

  upsertAppEnvironment = async (
    appId: string,
    environment: string,
    variables: Record<string, string>
  ) => {
    try {
      const { data } = await this.httpClient.request(
        upsertAppEnvironment({
          appEnvironment: {
            appProjectId: appId,
            name: environment,
            variables,
          },
        })
      );

      return AppEnvironmentSchema.parse(data).appEnvironment;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToSetEnvironmentVariable(),
        cause: e,
      });
    }
  };

  removeAppEnvironmentVariable = async (
    appId: string,
    environment: string,
    variable: string
  ) => {
    try {
      const appEnvironment = await this.getAppEnvironmentByName(
        appId,
        environment
      );

      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            removeAppEnvironmentVariables({
              appEnvironmentId: appEnvironment.id,
              appProjectId: appId,
              variables: [variable],
            })
          ),
        this.retryOptions
      );

      return AppEnvironmentSchema.parse(data).appEnvironment;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToRemoveEnvironmentVariable(),
        cause: e,
      });
    }
  };
}
